/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/invoiceData.scss';

import {ChangeKeyFunction, voidFunction} from '@dvrd/dvr-controls';

import React, {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import Invoice from '../../../models/invoiceModel';

import {BolmateInput} from '../../controls';

import CountryField from '../../controls/country/field/countryField';

import useRight from '../../misc/useRight';

import {AccessKey} from '../../../util/interfaces';

import EmailDisabledMessage from '../../misc/emailDisabledMessage';

interface Props {
    onChange?: ChangeKeyFunction;
    invoice: Invoice;
    disabled?: boolean;
    loading?: boolean;
}

export default function InvoiceData(props: Props) {
    const {t} = useTranslation();
    const [canEdit] = useRight(AccessKey.INVOICE_EDIT_CUSTOMER);
    const {loading, onChange, disabled, invoice} = props;
    const fieldsDisabled = disabled || loading || !canEdit;
    const onChangeField = useMemo(() => onChange ?? (() => voidFunction), [onChange]);

    const {
        orderId,
        company,
        firstname,
        lastname,
        email,
        street,
        houseNumber,
        houseNumberSuffix,
        zipCode,
        city,
        reference,
        cocNumber,
        vatNumber
    } = invoice;

    return (
        <div className='invoice-data'>
            <label className='col-title'>
                {t('invoice.data.title', 'Factuurgegevens')} ({t('invoice.data.order', 'order')} {orderId})
            </label>
            <div className='fields-container'>
                <div className='col'>
                    <BolmateInput
                        onChange={onChangeField('company')}
                        value={company}
                        label={t('invoice.data.company', 'Bedrijf')}
                        disabled={fieldsDisabled}
                    />
                    <BolmateInput
                        value={firstname}
                        onChange={onChangeField('firstname')}
                        label={t('invoice.data.firstName', 'Voornaam')}
                        disabled={fieldsDisabled}
                    />
                    <BolmateInput
                        value={lastname}
                        onChange={onChangeField('lastname')}
                        label={t('invoice.data.lastName', 'Achternaam')}
                        disabled={fieldsDisabled}
                    />
                    <BolmateInput
                        value={email}
                        onChange={onChangeField('email')}
                        label={t('invoice.data.email', 'E-mailadres')}
                        disabled
                        inputProps={{type: 'email'}}
                        ornaments={{
                            element: (
                                <EmailDisabledMessage>
                                    {t(
                                        'invoice.data.emailDisabled',
                                        'Vanwege veiligheid niet aan te passen. Je kunt de factuur downloaden en handmatig zelf mailen.'
                                    )}
                                </EmailDisabledMessage>
                            )
                        }}
                    />
                </div>
                <div className='col'>
                    <BolmateInput
                        value={street}
                        onChange={onChangeField('street')}
                        label={t('invoice.data.address', 'Adres')}
                        disabled={fieldsDisabled}
                    />
                    <div className='row'>
                        <BolmateInput
                            value={houseNumber}
                            onChange={onChangeField('house_number')}
                            label={t('invoice.data.houseNumber', 'Huisnummer')}
                            disabled={fieldsDisabled}
                        />
                        <BolmateInput
                            value={houseNumberSuffix}
                            onChange={onChangeField('house_number_suffix')}
                            label={t('invoice.data.suffix', 'Toevoeging')}
                            disabled={fieldsDisabled}
                            className='suffix'
                        />
                    </div>
                    <BolmateInput
                        value={zipCode}
                        onChange={onChangeField('zip_code')}
                        label={t('invoice.data.zipCode', 'Postcode')}
                        disabled={fieldsDisabled}
                    />
                    <BolmateInput
                        value={city}
                        onChange={onChangeField('city')}
                        label={t('invoice.data.city', 'Stad')}
                        disabled={fieldsDisabled}
                    />
                </div>
                <div className='col'>
                    <BolmateInput
                        value={reference}
                        onChange={onChangeField('reference')}
                        label={t('invoice.data.reference', 'Referentie')}
                        disabled={fieldsDisabled}
                    />
                    <BolmateInput
                        value={cocNumber}
                        onChange={onChangeField('coc_number')}
                        label={t('invoice.data.cocNumber', 'KvK-nummer')}
                        disabled={loading}
                        inputProps={{placeholder: t('invoice.data.cocPh', 'KvK nummer')}}
                    />
                    <BolmateInput
                        value={vatNumber}
                        onChange={onChangeField('vat_number')}
                        label={t('invoice.data.vatNumber', 'BTW-nummer')}
                        disabled={loading}
                        inputProps={{placeholder: t('invoice.data.vatPh', 'BTW nummer')}}
                    />
                    <CountryField
                        onChange={onChangeField('country')}
                        disabled={fieldsDisabled}
                        value={invoice.countryName}
                        labelAsValue
                    />
                </div>
            </div>
        </div>
    );
}
